import * as React from "react";
import { Cross2Icon } from "@radix-ui/react-icons";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@pma/utils/helpers";
import useTranslator from "@pma/hooks/use-translator";
import {
  CheckCircle2,
  Info,
  AlertTriangle,
  XCircle,
  AlertOctagon,
} from "lucide-react";

// ─── Shared Components ──────────────────────────────────────────────────────

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => {
  const { locale } = useTranslator();

  const isHebrew = locale === "he";

  return (
    <ToastPrimitives.Viewport
      ref={ref}
      className={cn(
        "fixed z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:flex-col md:max-w-[420px]",
        isHebrew ? "bottom-0 sm:left-0" : "bottom-0 sm:right-0",
        className,
      )}
      {...props}
    />
  );
});

ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

// ─── Toast Variants ─────────────────────────────────────────────────────────

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-center overflow-hidden rounded-lg border bg-white p-4 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
  {
    variants: {
      variant: {
        default: "border-gray-200 text-gray-900",
        destructive: "border-red-200 text-gray-900",
        success: "border-green-200 text-gray-900",
        info: "border-blue-200 text-gray-900",
        warning: "border-yellow-200 text-gray-900",
        error: "border-red-200 text-gray-900",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

// ─── Helpers (icon + progress bar) ──────────────────────────────────────────

const toastProgressColors: Record<string, string> = {
  default: "bg-gray-500",
  destructive: "bg-red-500",
  success: "bg-green-500",
  info: "bg-blue-500",
  warning: "bg-yellow-500",
  error: "bg-red-500",
};

const toastIcons: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  default: Info,
  destructive: AlertOctagon,
  success: CheckCircle2,
  info: Info,
  warning: AlertTriangle,
  error: XCircle,
};

const toastIconColors: Record<string, string> = {
  default: "text-gray-500",
  destructive: "text-red-500",
  success: "text-green-500",
  info: "text-blue-500",
  warning: "text-yellow-500",
  error: "text-red-500",
};

const ToastProgress = ({
  variant = "default",
}: {
  variant?: string | null;
}) => {
  const colorClass = toastProgressColors[variant || "default"] || "bg-gray-500";
  return (
    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-100">
      <div
        className={cn("h-full animate-toast-progress rounded-full", colorClass)}
      />
    </div>
  );
};

const ToastIcon = ({ variant = "default" }: { variant?: string | null }) => {
  const Icon = toastIcons[variant || "default"] || Info;
  const colorClass = toastIconColors[variant || "default"] || "text-gray-500";
  return <Icon className={cn("h-5 w-5 shrink-0", colorClass)} />;
};

// ─── Toast Root ─────────────────────────────────────────────────────────────

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants> & { toastDuration?: number }
>(({ className, variant, toastDuration = 4000, ...props }, ref) => {
    const { locale } = useTranslator();
  const isHebrew = locale === "he";
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className, isHebrew ? "!pl-8" : "!pr-8")}
      style={
        { "--toast-duration": `${toastDuration}ms` } as React.CSSProperties
      }
      {...props}
    />
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-1 focus:ring-ring disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive",
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => {
  const { locale } = useTranslator();
  const isHebrew = locale === "he";

  return (
    <ToastPrimitives.Close
      ref={ref}
      className={cn(
        "absolute top-2 rounded-md p-1 text-gray-400 transition-opacity hover:text-gray-600 focus:outline-none focus:ring-1",
        isHebrew ? "left-2" : "right-2",
        className,
      )}
      toast-close=""
      {...props}
    >
      <Cross2Icon className="h-4 w-4" />
    </ToastPrimitives.Close>
  );
});
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => {
  const { locale } = useTranslator();

  const isHebrew = locale === "he";
  return (
    <ToastPrimitives.Title
      ref={ref}
      className={cn(
        "text-sm font-semibold [&+div]:text-xs",
        isHebrew ? "ltr text-right" : "rtl",
        className,
      )}
      {...props}
    />
  );
});
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => {
  const { locale } = useTranslator();
  const isHebrew = locale === "he";
  return (
    <ToastPrimitives.Description
      ref={ref}
      className={cn(
        "text-sm opacity-90",
        isHebrew ? "rtl" : " ltr text-left",
        className,
      )}
      {...props}
    />
  );
});
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
  ToastProgress,
  ToastIcon,
};
